'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useState, useRef, useEffect, useCallback } from 'react';
import styles from '../styles/Home.module.css';

const TOKEN_API = process.env.NEXT_PUBLIC_TOKEN_API || 'https://book.minervafoundry.com/meet/token';
const LIVEKIT_URL = process.env.NEXT_PUBLIC_LIVEKIT_URL || 'wss://meet.minervafoundry.com';

/* ── Shared styles ─────────────────────────────────────────────── */
const selectStyle: React.CSSProperties = {
  width: '100%', padding: '0.5rem 0.75rem', borderRadius: '8px',
  border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)',
  color: '#fff', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  appearance: 'none' as const,
};

const toggleBtnBase: React.CSSProperties = {
  width: 48, height: 48, borderRadius: '50%', border: 'none',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0,
};

/* ── SVG icons (inline, no deps) ───────────────────────────────── */
const MicOn = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>
  </svg>
);
const MicOff = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" x2="22" y1="2" y2="22"/>
    <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 0"/>
    <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/>
    <line x1="12" x2="12" y1="19" y2="22"/>
  </svg>
);
const CamOn = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/>
    <rect x="2" y="6" width="14" height="12" rx="2"/>
  </svg>
);
const CamOff = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" x2="22" y1="2" y2="22"/>
    <path d="M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196"/>
    <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1"/>
  </svg>
);

/* ── Audio level meter hook ────────────────────────────────────── */
function useAudioLevel(stream: MediaStream | null) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!stream) { setLevel(0); return; }
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) { setLevel(0); return; }
    let raf: number;
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.5;
    src.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      setLevel(Math.min(1, (sum / data.length) / 80));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); ctx.close(); };
  }, [stream]);
  return level;
}

/* ── Device enumeration hook ───────────────────────────────────── */
function useDevices() {
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const refresh = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameras(devices.filter((d) => d.kind === 'videoinput'));
      setMics(devices.filter((d) => d.kind === 'audioinput'));
    } catch {}
  }, []);
  useEffect(() => {
    refresh();
    navigator.mediaDevices.addEventListener('devicechange', refresh);
    return () => navigator.mediaDevices.removeEventListener('devicechange', refresh);
  }, [refresh]);
  return { cameras, mics, refresh };
}

/* ── Main lobby component ──────────────────────────────────────── */
function PreJoinLobby() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const room = searchParams?.get('room');
  const debugTimer = searchParams?.get('debugTimer');

  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);

  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [selectedCam, setSelectedCam] = useState('');
  const [selectedMic, setSelectedMic] = useState('');

  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [lobbyDurationMin, setLobbyDurationMin] = useState<number | null>(null);

  const { cameras, mics, refresh: refreshDevices } = useDevices();
  const audioLevel = useAudioLevel(audioOn ? audioStream : null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!room) return;
    let cancelled = false;
    fetch(TOKEN_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ room }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.duration_min) setLobbyDurationMin(data.duration_min);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [room]);

  /* Acquire / release video stream */
  useEffect(() => {
    if (!room || !videoOn) {
      videoStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      setVideoStream(null);
      return;
    }
    let cancelled = false;
    const constraints: MediaStreamConstraints = {
      video: selectedCam ? { deviceId: { exact: selectedCam } } : true,
      audio: false,
    };
    navigator.mediaDevices.getUserMedia(constraints)
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        setVideoStream(s);
        refreshDevices();
      })
      .catch(() => setVideoStream(null));
    return () => { cancelled = true; };
  }, [room, videoOn, selectedCam]);

  /* Acquire / release audio stream */
  useEffect(() => {
    if (!room || !audioOn) {
      audioStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      setAudioStream(null);
      return;
    }
    let cancelled = false;
    const constraints: MediaStreamConstraints = {
      video: false,
      audio: selectedMic ? { deviceId: { exact: selectedMic } } : true,
    };
    navigator.mediaDevices.getUserMedia(constraints)
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        setAudioStream(s);
        refreshDevices();
      })
      .catch(() => setAudioStream(null));
    return () => { cancelled = true; };
  }, [room, audioOn, selectedMic]);

  /* Bind video element */
  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = videoStream || null;
  }, [videoStream]);

  /* Clean up on unmount */
  useEffect(() => {
    return () => {
      videoStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      audioStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    };
  }, []);

  const handleJoin = useCallback(async () => {
    if (!room || joining) return;
    setJoining(true);
    setError('');
    try {
      const res = await fetch(TOKEN_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, identity: name.trim() || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      const { token, livekit_url, start_ts_utc, duration_min } = await res.json();
      videoStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      audioStream?.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      const params = new URLSearchParams({
        liveKitUrl: livekit_url || LIVEKIT_URL,
        token,
        audioEnabled: String(audioOn),
        videoEnabled: String(videoOn),
        singlePC: 'true',
      });
      if (start_ts_utc) params.set('startTsUtc', start_ts_utc);
      if (duration_min) params.set('durationMin', String(duration_min));
      if (debugTimer) params.set('debugTimer', debugTimer);
      router.replace(`/custom/?${params.toString()}`);
    } catch (err: any) {
      setError(err.message || 'Failed to join');
      setJoining(false);
    }
  }, [room, name, joining, router, videoStream, audioStream, audioOn, videoOn, debugTimer]);

  /* ── Invalid / missing room ──────────────────────────────────── */
  if (!mounted) {
    return (
      <main className={styles.main} data-lk-theme="default">
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Meeting Room</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '400px', lineHeight: '1.6' }}>
            Loading…
          </p>
        </div>
      </main>
    );
  }

  if (!room) {
    return (
      <main className={styles.main} data-lk-theme="default">
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Meeting Room</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '400px', lineHeight: '1.6' }}>
            This link is invalid or has expired.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', marginTop: '1rem' }}>
            If you received a meeting link, please check that you copied the full URL.
          </p>
        </div>
      </main>
    );
  }

  /* ── Lobby UI ────────────────────────────────────────────────── */
  return (
    <main className={styles.main} data-lk-theme="default">
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: '1rem', maxWidth: '440px', width: '100%', padding: '0 1rem',
      }}>
        <h1 style={{ fontSize: '1.5rem', margin: 0 }}>Join Meeting</h1>

        {/* Video preview */}
        <div style={{
          width: '100%', aspectRatio: '4/3', borderRadius: '12px',
          overflow: 'hidden', background: '#1a1a1a', position: 'relative',
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)',
              display: videoOn && videoStream ? 'block' : 'none',
            }}
          />
          {(!videoOn || !videoStream) && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem',
            }}>
              {videoOn ? 'Starting camera…' : 'Camera off'}
            </div>
          )}
        </div>

        {/* Toggle buttons + audio meter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', justifyContent: 'center' }}>
          <button
            onClick={() => setAudioOn((v: boolean) => !v)}
            style={{ ...toggleBtnBase, background: audioOn ? 'rgba(255,255,255,0.12)' : '#dc2626', color: '#fff' }}
            title={audioOn ? 'Mute microphone' : 'Unmute microphone'}
          >
            {audioOn ? <MicOn /> : <MicOff />}
          </button>
          <button
            onClick={() => setVideoOn((v: boolean) => !v)}
            style={{ ...toggleBtnBase, background: videoOn ? 'rgba(255,255,255,0.12)' : '#dc2626', color: '#fff' }}
            title={videoOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {videoOn ? <CamOn /> : <CamOff />}
          </button>

          {/* Audio level meter */}
          <div style={{
            flex: 1, height: 6, borderRadius: 3,
            background: 'rgba(255,255,255,0.1)', overflow: 'hidden', maxWidth: 120,
          }}>
            <div style={{
              height: '100%', borderRadius: 3,
              background: audioLevel > 0.6 ? '#22c55e' : audioLevel > 0.3 ? '#eab308' : '#4ade80',
              width: `${Math.round(audioLevel * 100)}%`,
              transition: 'width 0.08s ease-out',
            }} />
          </div>
        </div>

        {/* Device pickers */}
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
          <select
            value={selectedCam}
            onChange={(e) => setSelectedCam(e.target.value)}
            disabled={!videoOn}
            style={{ ...selectStyle, opacity: videoOn ? 1 : 0.4 }}
          >
            <option value="">Default camera</option>
            {cameras.map((d: MediaDeviceInfo) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Camera ${cameras.indexOf(d) + 1}`}
              </option>
            ))}
          </select>
          <select
            value={selectedMic}
            onChange={(e) => setSelectedMic(e.target.value)}
            disabled={!audioOn}
            style={{ ...selectStyle, opacity: audioOn ? 1 : 0.4 }}
          >
            <option value="">Default mic</option>
            {mics.map((d: MediaDeviceInfo) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Mic ${mics.indexOf(d) + 1}`}
              </option>
            ))}
          </select>
        </div>

        {/* Auto-disconnect explainer card */}
        {lobbyDurationMin !== null && (
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            fontSize: '0.8125rem',
            lineHeight: '1.65',
            color: 'rgba(255,255,255,0.75)',
          }}>
            <div style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'rgba(255,255,255,0.45)',
              marginBottom: '0.5rem',
            }}>
              Beta: auto-ending conversations
            </div>
            <p style={{ margin: '0 0 0.6rem 0' }}>
              I get all in my head about scheduling, taking up more time than I asked from people, and ending convos during the chat. Which leads to me scheduling chats less often and being distracted while we&apos;re enjoying each other&apos;s company.
            </p>
            <p style={{ margin: '0 0 0.6rem 0' }}>
              So, I built a feature that ends convos on time. We&apos;ll get three messages, at 5 minutes, 2 minutes, and 30 seconds remaining, to remind us when we&apos;re close. And so as to not make it stressful, you&apos;ll also notice a background color shift from a day to night color palette.
            </p>
            <p style={{ margin: '0 0 0.6rem 0', fontWeight: 500, color: 'rgba(255,255,255,0.85)' }}>
              This chat is scheduled to end in {lobbyDurationMin} minutes.
            </p>
            <p style={{ margin: 0 }}>
              Let me know what you think either during the call or after — I&apos;d love to know how it worked or didn&apos;t for you.
            </p>
          </div>
        )}

        {/* Name input */}
        <input
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleJoin(); }}
          maxLength={64}
          disabled={joining}
          style={{
            width: '100%', padding: '0.75rem 1rem', borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)',
            color: '#fff', fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
          }}
        />

        {/* Join button */}
        <button
          onClick={handleJoin}
          disabled={joining}
          style={{
            width: '100%', padding: '0.75rem', borderRadius: '8px',
            border: 'none', background: joining ? '#444' : '#2563eb',
            color: '#fff', fontSize: '1rem', fontWeight: 600,
            cursor: joining ? 'wait' : 'pointer', transition: 'background 0.15s',
          }}
        >
          {joining ? 'Joining…' : 'Join'}
        </button>

        {error && (
          <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: 0 }}>{error}</p>
        )}
      </div>
    </main>
  );
}

export default function Page() {
  return (
    <Suspense fallback={<main className={styles.main} data-lk-theme="default"><p>Loading...</p></main>}>
      <PreJoinLobby />
    </Suspense>
  );
}
